import { useState, useEffect, useCallback } from "react";
import { Modal, Box, Text, Tabs, Group, Textarea, LoadingOverlay } from "@mantine/core";
import { Badge, Button } from "@xplatform/design-system";
import { IconRobot, IconDeviceFloppy } from "@tabler/icons-react";
import type {
    AgentExecutionViewModel,
    FlowConfigViewModel,
    FlowStepViewModel,
} from "@models/doctype";
import type { ProviderViewModel } from "@models/provider";
import type { ModelBasicInfoViewModel, ModelViewModel } from "@models/model";
import { useFlowConfig } from "@hooks/flow/useFlowConfig";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { useTranslation } from "@hooks/useTranslation";
import { useToast } from "@hooks/useToast";
import { ProviderModelSelect } from "../ProviderModelSelect";
import { ConfigOptionsFields } from "../ConfigOptionsFields";
import { buildAgentUpdateRequest, buildConfigOptionsFromForm } from "@utils/flowConfigHelpers";
import "./AgentDetailModal.scss";

interface AgentDetailModalProps {
    opened: boolean;
    onClose: () => void;
    agent: AgentExecutionViewModel;
    step: FlowStepViewModel;
    flowConfig: FlowConfigViewModel;
    docTypeId: string;
    onSave?: () => void;
}

export const AgentDetailModal = ({
    opened,
    onClose,
    agent,
    step,
    flowConfig,
    docTypeId,
    onSave,
}: AgentDetailModalProps) => {
    const { t } = useTranslation();
    const { showSuccess, showError } = useToast();
    const { canCreateDoctype } = useAdminPermissions();
    const { createFlowConfig, fetchProviders, fetchModelsBasicByProviderKey, fetchModelDetailByName, isLoading } = useFlowConfig();

    const [systemPrompt, setSystemPrompt] = useState("");
    const [userPrompt, setUserPrompt] = useState("");
    const [responseSchema, setResponseSchema] = useState("");
    const [modelName, setModelName] = useState("");
    const [temperature, setTemperature] = useState<number | string>("");
    const [topP, setTopP] = useState<number | string>("");
    const [topK, setTopK] = useState<number | string>("");
    const [maxOutputTokens, setMaxOutputTokens] = useState<number | string>("");
    const [thinkingBudget, setThinkingBudget] = useState<number | string>("");
    const [seed, setSeed] = useState<number | string>("");
    const [hasChanges, setHasChanges] = useState(false);

    // Provider/Model state
    const [providers, setProviders] = useState<ProviderViewModel[]>([]);
    const [models, setModels] = useState<ModelBasicInfoViewModel[]>([]);
    const [selectedProvider, setSelectedProvider] = useState("");
    const [selectedModel, setSelectedModel] = useState<ModelViewModel | null>(null);
    const [isLoadingProviders, setIsLoadingProviders] = useState(false);
    const [isLoadingModels, setIsLoadingModels] = useState(false);

    const loadModels = useCallback(async (providerKey: string) => {
        setIsLoadingModels(true);
        try {
            const loadedModels = await fetchModelsBasicByProviderKey(providerKey);
            setModels(loadedModels);
        } finally {
            setIsLoadingModels(false);
        }
    }, [fetchModelsBasicByProviderKey]);

    useEffect(() => {
        if (!opened) return;

        const init = async () => {
            setIsLoadingProviders(true);
            try {
                if (!canCreateDoctype) {
                    // Read-only: set provider/model from step config directly
                    const parentProvider = step.llmStepConfig?.provider || "";
                    setSelectedProvider(parentProvider);
                    const agentModel = agent.modelName || step.llmStepConfig?.modelName;
                    if (agentModel) {
                        setSelectedModel({
                            modelName: agentModel,
                            displayName: agentModel,
                        } as ModelViewModel);
                    }
                    return;
                }

                const loadedProviders = await fetchProviders("llm");
                setProviders(loadedProviders);

                // Find the step's provider to pre-select and load its models
                const parentProvider = step.llmStepConfig?.provider;
                const agentProvider = parentProvider; // agents inherit provider from step
                const providerObj = loadedProviders.find((p) => p.key === agentProvider);
                setSelectedProvider(providerObj?.id || "");

                if (agentProvider) {
                    await loadModels(agentProvider);
                }

                // Load model detail if agent has a model
                const agentModel = agent.modelName || step.llmStepConfig?.modelName;
                if (agentModel) {
                    const detail = await fetchModelDetailByName(agentModel);
                    setSelectedModel(detail);
                }
            } finally {
                setIsLoadingProviders(false);
            }
        };

        setSystemPrompt(agent.systemPrompt ?? "");
        setUserPrompt(agent.userPrompt ?? "");
        setResponseSchema(agent.responseSchema ? JSON.stringify(agent.responseSchema, null, 2) : "");
        setModelName(agent.modelName ?? "");
        setTemperature(agent.configOptions?.temperature ?? "");
        setTopP(agent.configOptions?.topP ?? "");
        setTopK(agent.configOptions?.topK ?? "");
        setMaxOutputTokens(agent.configOptions?.maxOutputTokens ?? "");
        setThinkingBudget(agent.configOptions?.thinkingBudget ?? "");
        setSeed(agent.configOptions?.seed ?? "");
        setHasChanges(false);

        init();
    }, [agent, step, opened, fetchProviders, fetchModelDetailByName, loadModels]);

    const markChanged = () => { if (!hasChanges) setHasChanges(true); };

    const handleProviderChange = async (providerId: string) => {
        setSelectedProvider(providerId);
        setSelectedModel(null);
        setModelName("");
        markChanged();

        const providerObj = providers.find((p) => p.id === providerId);
        if (providerObj) {
            await loadModels(providerObj.key);
        }
    };

    const handleModelChange = async (selectedModelName: string) => {
        setModelName(selectedModelName);
        markChanged();

        if (selectedModelName) {
            setIsLoadingModels(true);
            try {
                const detail = await fetchModelDetailByName(selectedModelName);
                setSelectedModel(detail);
            } finally {
                setIsLoadingModels(false);
            }
        } else {
            setSelectedModel(null);
        }
    };

    const handleSave = async () => {
        const request = buildAgentUpdateRequest(flowConfig, step, agent, {
            domainKey: agent.domainKey,
            userPrompt,
            responseSchema: responseSchema.trim() ? JSON.parse(responseSchema) : null,
            responseSchemaText: responseSchema,
            systemPrompt,
            modelName,
            configOptions: buildConfigOptionsFromForm({ temperature, topP, topK, maxOutputTokens, thinkingBudget, seed }),
        });

        const result = await createFlowConfig(docTypeId, request);
        if (result) {
            setHasChanges(false);
            showSuccess(t("admin.flowConfigs.messages.saveSuccess"));
            onSave?.();
            onClose();
        } else {
            showError(t("admin.flowConfigs.messages.saveFailed"));
        }
    };

    const parentConfig = step.llmStepConfig!;
    const tabLabels = [
        t("admin.flowConfigs.labels.llmConfig"),
        t("admin.flowConfigs.labels.systemPrompt"),
        t("admin.flowConfigs.labels.userPrompt"),
        t("admin.flowConfigs.labels.responseSchema"),
    ];

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            size="95%"
            className="agent-detail-modal"
            portalProps={{ target: "#admin-portal-root" }}
            styles={{
                content: { maxHeight: "calc(100vh - 60px)", display: "flex", flexDirection: "column" },
                header: { flexShrink: 0 },
                body: { display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", paddingBottom: 0 },
            }}
            title={
                <Box>
                    <Group gap="sm">
                        <IconRobot size={22} color="var(--mantine-color-violet-6)" />
                        <Text fw={700} size="lg">{agent.domainKey}</Text>
                    </Group>
                    {hasChanges && <Text size="xs" c="yellow">Unsaved changes</Text>}
                </Box>
            }
        >
            <LoadingOverlay visible={isLoading} />
            <Box className="agent-detail-modal__content">
                <Tabs defaultValue="0" className="agent-detail-modal__tabs">
                    <Tabs.List>
                        {tabLabels.map((label, i) => (
                            <Tabs.Tab key={i} value={String(i)}>{label}</Tabs.Tab>
                        ))}
                    </Tabs.List>

                    <Tabs.Panel value="0" className="agent-detail-modal__panel">
                        <Box className="agent-detail-modal__config-section">
                            <ProviderModelSelect
                                isLoadingProviders={isLoadingProviders}
                                isLoadingModels={isLoadingModels}
                                providers={providers}
                                models={models}
                                selectedProvider={selectedProvider}
                                selectedModel={selectedModel}
                                onProviderChange={handleProviderChange}
                                onModelChange={handleModelChange}
                            />
                        </Box>
                        <Text size="sm" fw={600} mt="md" mb="xs">Configuration Options</Text>
                        <ConfigOptionsFields
                            temperature={temperature}
                            topP={topP}
                            topK={topK}
                            maxOutputTokens={maxOutputTokens}
                            thinkingBudget={thinkingBudget}
                            seed={seed}
                            onTemperatureChange={(v) => { setTemperature(v); markChanged(); }}
                            onTopPChange={(v) => { setTopP(v); markChanged(); }}
                            onTopKChange={(v) => { setTopK(v); markChanged(); }}
                            onMaxOutputTokensChange={(v) => { setMaxOutputTokens(v); markChanged(); }}
                            onThinkingBudgetChange={(v) => { setThinkingBudget(v); markChanged(); }}
                            onSeedChange={(v) => { setSeed(v); markChanged(); }}
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="1" className="agent-detail-modal__panel">
                        <Group gap="xs" mb="sm">
                            <Badge size="sm" variant="light" color={systemPrompt ? "violet" : "gray"}>
                                {systemPrompt ? "agent override" : "Using step default when empty"}
                            </Badge>
                        </Group>
                        <Textarea
                            label={t("admin.flowConfigs.labels.systemPrompt")}
                            value={systemPrompt}
                            onChange={(e) => { setSystemPrompt(e.currentTarget.value); markChanged(); }}
                            placeholder={parentConfig.systemPrompt || "Enter system prompt override..."}
                            minRows={30}
                            className="agent-detail-modal__prompt-field"
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="2" className="agent-detail-modal__panel">
                        <Textarea
                            label={t("admin.flowConfigs.labels.userPrompt")}
                            value={userPrompt}
                            onChange={(e) => { setUserPrompt(e.currentTarget.value); markChanged(); }}
                            placeholder="Enter user prompt..."
                            required
                            minRows={30}
                            className="agent-detail-modal__prompt-field"
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="3" className="agent-detail-modal__panel">
                        <Textarea
                            label={t("admin.flowConfigs.labels.responseSchema") + " (Optional)"}
                            value={responseSchema}
                            onChange={(e) => { setResponseSchema(e.currentTarget.value); markChanged(); }}
                            placeholder='{"type": "object", "properties": {...}}'
                            minRows={30}
                            className="agent-detail-modal__prompt-field"
                            styles={{ input: { fontFamily: "monospace", fontSize: "0.85rem" } }}
                        />
                    </Tabs.Panel>
                </Tabs>
            </Box>

            <Group justify="flex-end" mt="md" className="agent-detail-modal__actions">
                <Button variant="subtle" onClick={onClose} disabled={isLoading}>
                    {t("common.cancel")}
                </Button>
                <Button
                    onClick={handleSave}
                    leftSection={<IconDeviceFloppy size={16} />}
                    disabled={isLoading || !hasChanges}
                >
                    {t("common.save")}
                </Button>
            </Group>
        </Modal>
    );
};
